import {
  getTokenFromLocalStorage,
  getRefreshTokenFromLocalStorage,
  setItemToLacalStorage,
  removeAllTokens,
} from '../helpers/localstorage.helper';
import { store } from '../store/store';
import { AuthService } from '../services/auth.service';
import { logout, setRefreshing } from '../store/user/userSlice';

const baseURL = store.getState().config.baseURL;

let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface RequestConfig {
  method: HttpMethod;
  path: string;
  body?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  _retry?: boolean;
}

interface FetchResponse<T> {
  data: T;
}

function buildUrl(base: string, path: string, params?: Record<string, unknown>): string {
  const separator = base.endsWith('/') || path.startsWith('/') ? '' : '/';
  let url = base + separator + path;
  if (params) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ),
    ).toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }
  return url;
}

async function parseBody(response: Response): Promise<unknown> {
  const ct = response.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return response.json();
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function makeRequest<T>(cfg: RequestConfig, base: string): Promise<FetchResponse<T>> {
  const headers: Record<string, string> = { ...(cfg.headers ?? {}) };

  if (!headers['Authorization']) {
    const token = getTokenFromLocalStorage();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const url = buildUrl(base, cfg.path, cfg.params);

  let fetchBody: BodyInit | undefined;
  if (cfg.body !== undefined) {
    if (cfg.body instanceof FormData) {
      fetchBody = cfg.body;
      delete headers['Content-Type'];
    } else {
      fetchBody = JSON.stringify(cfg.body);
      if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';
    }
  }

  let response: Response;
  try {
    response = await fetch(url, { method: cfg.method, headers, body: fetchBody, signal: cfg.signal });
  } catch (networkError) {
    throw networkError;
  }

  const data = await parseBody(response);

  if (response.ok) {
    return { data: data as T };
  }

  const errorMsg: string = (data as any)?.error ?? (data as any)?.message ?? '';

  const error: any = new Error(`Request failed with status ${response.status}`);
  error.response = { data, status: response.status };

  if (errorMsg.includes('Invalid token') || errorMsg === 'Invalid token') {
    store.dispatch(logout());
    removeAllTokens();
    window.location.href = '/';
    throw error;
  }

  if (response.status === 401 && !cfg._retry) {
    if (errorMsg === 'User not found' || errorMsg.includes('Invalid token')) {
      store.dispatch(logout());
      removeAllTokens();
      window.location.href = '/';
      throw error;
    }

    if (isRefreshing) {
      return new Promise<FetchResponse<T>>((resolve, reject) => {
        failedQueue.push({
          resolve: (token) =>
            resolve(
              makeRequest<T>({ ...cfg, _retry: true, headers: { ...headers, Authorization: `Bearer ${token}` } }, base),
            ),
          reject,
        });
      });
    }

    cfg._retry = true;
    isRefreshing = true;
    store.dispatch(setRefreshing(true));

    try {
      const refreshToken = getRefreshTokenFromLocalStorage();
      if (!refreshToken) {
        store.dispatch(logout());
        removeAllTokens();
        processQueue(new Error('Refresh token not available'));
        window.location.href = '/';
        throw error;
      }

      const refreshed = await AuthService.refreshToken(refreshToken);
      setItemToLacalStorage('access_token', refreshed.access_token);
      setItemToLacalStorage('refresh_token', refreshed.refresh_token);
      processQueue(null, refreshed.access_token);

      return makeRequest<T>(
        { ...cfg, headers: { ...headers, Authorization: `Bearer ${refreshed.access_token}` } },
        base,
      );
    } catch (refreshError) {
      store.dispatch(logout());
      removeAllTokens();
      processQueue(refreshError);
      window.location.href = '/';
      throw refreshError;
    } finally {
      isRefreshing = false;
      store.dispatch(setRefreshing(false));
    }
  }

  throw error;
}

function createClient(base: string) {
  return {
    get: <T = unknown>(path: string, config?: { params?: Record<string, unknown>; headers?: Record<string, string> }) =>
      makeRequest<T>({ method: 'GET', path, params: config?.params, headers: config?.headers }, base),

    post: <T = unknown>(
      path: string,
      body?: unknown,
      config?: { headers?: Record<string, string>; signal?: AbortSignal },
    ) => makeRequest<T>({ method: 'POST', path, body, headers: config?.headers, signal: config?.signal }, base),

    put: <T = unknown>(path: string, body?: unknown, config?: { headers?: Record<string, string> }) =>
      makeRequest<T>({ method: 'PUT', path, body, headers: config?.headers }, base),

    delete: <T = unknown>(path: string, config?: { headers?: Record<string, string> }) =>
      makeRequest<T>({ method: 'DELETE', path, headers: config?.headers }, base),
  };
}

export const authInstance = createClient(baseURL);
export const instance = createClient(baseURL + '/api');
